// Calibrate (staff): save a monitor's brightness baseline with the lux meter.
//
// /patients/:id/calibrate  the patient's home monitor (at kit set-up, or on their computer)
// /displays/:id/calibrate  a clinic station's display
//
// There is no photometer: the baseline is what the kit's lux meter reads, pressed against
// the middle of the white screen, on this monitor at the settings it will be used with.
// Later sessions on the monitor are compared with it (±10 %). Setting a new one retires the
// old one; both stay in the list below.

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, ApiError, type LuxBaseline, type LuxIdentity, type LuxVerdict } from "../api";
import { LuxGuide } from "../components/LuxGuide";
import { errorText, useLoad } from "../hooks";
import { probe, screenDetails } from "../lib/probe";

export function CalibrateMonitorPage({ target }: { target: "patient" | "display" }) {
  const id = Number(useParams().id);
  const t = target === "patient" ? { patient_id: id } : { display_id: id };
  const who = useLoad(() => (target === "patient" ? api.patient(id).then((p) => `patient ${p.study_id}`)
    : api.display(id).then((d) => `display ${d.label || d.browser_label || d.id}`)), [target, id]);
  const list = useLoad(() => (target === "patient" ? api.patientBaselines(id) : api.displayBaselines(id)), [target, id]);
  const [ident, setIdent] = useState<LuxIdentity | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<(LuxBaseline & { message: string }) | null>(null);
  const [run, setRun] = useState(0);

  const identify = async () => {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const d = await screenDetails();
      const r = await probe(d?.current ?? null);
      setIdent(await api.luxIdentify(r, t));
      setRun(run + 1);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  // The server validates the reading and saves the baseline in one step; a reading it
  // refuses (it makes no sense for a white screen) comes back as the guide's verdict.
  const judge = async (readings: number[]): Promise<LuxVerdict> => {
    if (!ident) throw new Error("identify the screen first");
    try {
      const b = await api.setBaseline(t, {
        readings_lux: readings, edid_key: ident.edid_key, screen_key: ident.screen_key, monitor: ident.monitor,
        notes: { previous_baseline_id: ident.baseline?.id ?? null },
      });
      setSaved(b);
      list.reload();
      return { verdict: "ok", message: b.message, mean_lux: b.white_lux };
    } catch (e) {
      if (e instanceof ApiError && e.status === 400 && e.detail && typeof e.detail === "object") {
        const d = e.detail as { verdict?: LuxVerdict["verdict"]; message?: string };
        if (d.verdict && d.message) return { verdict: d.verdict, message: d.message };
      }
      throw e;
    }
  };

  const prev = ident?.baseline;
  const change = saved && prev?.white_lux ? ((saved.white_lux! - prev.white_lux) / prev.white_lux) * 100 : null;
  const m = ident?.monitor;

  return (
    <div className="home-test" style={{ maxWidth: 820 }}>
      <h1>Calibrate a monitor (lux meter)</h1>
      <p className="small muted">{who.data ?? ""}{target === "patient" && <> · <Link to={`/patients/${id}`}>back to the patient</Link></>}
        {target === "display" && <> · <Link to="/stations">back to stations</Link></>}</p>
      <div className="card">
        <ol className="steps">
          <li>Connect the monitor{target === "patient" ? " the patient will test on" : ""} and set it up exactly as it will be
            used (picture mode, brightness, HDR off). Switch it on 15 minutes before measuring.</li>
          <li>Move this window onto that monitor and click <strong>Identify this screen</strong>.</li>
          <li>Follow the guide with the kit’s meter. Three readings that agree become the monitor’s baseline.</li>
        </ol>
        <button className="big" disabled={busy} onClick={() => void identify()}>{ident ? "Identify again" : "Identify this screen"}</button>
        <p className="small muted">If Chrome asks to “manage windows on all your displays”, choose Allow.</p>
        {error && <p className="error">{error}</p>}
      </div>

      {ident && m && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>{m.name || "This screen"}</h2>
          <p className="small">{m.screen_px?.[0]}×{m.screen_px?.[1]} px
            {m.manufacturer ? ` · ${m.manufacturer} ${m.product_code ?? ""}` : ""}{m.serial ? ` · serial ${m.serial}` : ""}
            {" · "}{m.approved_model ? <>approved model <strong>{m.approved_model}</strong></> : <span className="warn">not an approved model</span>}</p>
          {!ident.edid_read && (
            <p className="small warn">This monitor’s EDID has not been read on this computer, so the baseline is tied to its name and
              resolution (shared by identical monitors). To tie it to this unit’s serial number, run <em>Check my monitor</em>
              {" "}(<a href={api.monitorHelperUrl("mac")}>Mac</a> · <a href={api.monitorHelperUrl("windows")}>Windows</a>) and identify again.</p>
          )}
          {prev ? (
            <p className="small">Current baseline: <strong>{prev.white_lux} lux</strong>, {prev.set_at ? new Date(prev.set_at).toLocaleString() : ""}
              {" "}({prev.source === "staff_calibrate" ? "set by staff" : "first home session"}). A new one replaces it.</p>
          ) : <p className="small">No baseline yet for this monitor.</p>}
        </div>
      )}

      {ident && !saved && (
        <LuxGuide key={run} variant="full"
          title="Staff calibration: this reading becomes the monitor’s baseline."
          judge={judge} onDone={() => undefined} />
      )}
      {saved && (
        <div className="card ok-card">
          <h2 style={{ marginTop: 0 }}>Baseline saved: {saved.white_lux} lux</h2>
          {change !== null && <p>Previous baseline {prev?.white_lux} lux: <strong>{change >= 0 ? "+" : ""}{change.toFixed(1)}%</strong>.</p>}
          <button className="secondary" onClick={() => { setSaved(null); setRun(run + 1); }}>Measure again</button>
        </div>
      )}

      <h2>Baselines</h2>
      <div className="card">
        {list.data?.length ? (
          <table>
            <thead><tr><th>Set</th><th>Monitor</th><th>Lux</th><th>By</th><th /></tr></thead>
            <tbody>
              {list.data.map((b) => (
                <tr key={b.id} className={b.active ? "" : "muted"}>
                  <td className="small">{b.set_at ? new Date(b.set_at).toLocaleString() : ""}</td>
                  <td className="small">{b.monitor?.name ?? b.screen_key}{b.monitor?.serial ? `, serial ${b.monitor.serial}` : ""}</td>
                  <td><strong>{b.white_lux}</strong></td>
                  <td className="small">{b.set_by ?? "?"} ({b.source === "staff_calibrate" ? "Calibrate" : "first home session"})</td>
                  <td className="small">{b.active ? "current" : `replaced ${b.retired_at ? new Date(b.retired_at).toLocaleDateString() : ""}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p className="muted">None yet.</p>}
      </div>
    </div>
  );
}
